import React, { useState, useEffect } from 'react';
import challengeConfig from '../config/challengeConfig';
import { PageContainer, ColumnContainer, SectionHeader, Checkbox, Input, Label, Form } from './index';
import { getPreferences, savePreferences } from '../utils/preferencesUtils';

const getVersionForBlock = (blockNumber) => {
  const versionInfo = challengeConfig.versions.find(v => blockNumber >= v.start && blockNumber <= v.stop);
  return versionInfo ? versionInfo.version : null;
};

const PreferencesView = ({ provider }) => {
  const [preferences, setPreferences] = useState({
    gamerTag: '',
    difficulty: [],
    primary: [],
    secondary: [],
    grenade: [],
    stratagem: [],
  });
  const [versionConfig, setVersionConfig] = useState({});
  const [checkAll, setCheckAll] = useState(false);
  const [iconPaths, setIconPaths] = useState({ primary: {}, secondary: {}, grenade: {}, stratagem: {} });

  // Define an array of valid categories
  const validCategories = ['difficulty', 'primary', 'secondary', 'grenade', 'stratagem'];

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const blockNumber = await provider.getBlockNumber();
        const version = getVersionForBlock(blockNumber);
        const config = challengeConfig[version] || {};
        let storedPreferences = getPreferences();
        setPreferences(storedPreferences);
        setVersionConfig(config);

        // Load icons for all items in each category
        const loadIcons = async () => {
          const loaded = { primary: {}, secondary: {}, grenade: {}, stratagem: {} };
          // Primary
          if (config.primary) {
            for (const item of config.primary) {
              const path = challengeConfig.weapons.primary[item];
              if (path) {
                try {
                  const mod = await import(`../assets/helldivers2/weapons/${path}`);
                  loaded.primary[item] = mod.default;
                } catch (e) {
                  loaded.primary[item] = null;
                }
              }
            }
          }
          // Secondary
          if (config.secondary) {
            for (const item of config.secondary) {
              const path = challengeConfig.weapons.secondary[item];
              if (path) {
                try {
                  const mod = await import(`../assets/helldivers2/weapons/${path}`);
                  loaded.secondary[item] = mod.default;
                } catch (e) {
                  loaded.secondary[item] = null;
                }
              }
            }
          }
          // Grenade
          if (config.grenade) {
            for (const item of config.grenade) {
              const path = challengeConfig.weapons.grenade[item];
              if (path) {
                try {
                  const mod = await import(`../assets/helldivers2/weapons/${path}`);
                  loaded.grenade[item] = mod.default;
                } catch (e) {
                  loaded.grenade[item] = null;
                }
              }
            }
          }
          // Stratagem
          if (config.stratagem) {
            for (const item of config.stratagem) {
              const path = challengeConfig.stratagems[item];
              if (path) {
                try {
                  const mod = await import(`../assets/helldivers2/icons/Helldivers-2-Stratagems-icons-svg/${path}`);
                  loaded.stratagem[item] = mod.default;
                } catch (e) {
                  loaded.stratagem[item] = null;
                }
              }
            }
          }
          setIconPaths(loaded);
        };
        loadIcons();
      } catch (error) {
        console.error('Error fetching preferences:', error);
      }
    };
    fetchPreferences();
  }, [provider]);

  const handleChange = (category, index) => {
    const updatedPreferences = { ...preferences };
    updatedPreferences[category][index] = !preferences[category][index];
    setPreferences(updatedPreferences);
    savePreferences(updatedPreferences);
  };

  const handleGamerTagChange = (event) => {
    const updatedPreferences = { ...preferences, gamerTag: event.target.value };
    setPreferences(updatedPreferences);
    savePreferences(updatedPreferences);
  };

  const handleCheckAll = () => {
    const updatedPreferences = { ...preferences };
    validCategories.forEach(category => {
      if (versionConfig[category]) {
        updatedPreferences[category] = versionConfig[category].map(() => !checkAll);
      }
    });
    setPreferences(updatedPreferences);
    savePreferences(updatedPreferences);
    setCheckAll(!checkAll);
  };

  return (
    <PageContainer>
      <ColumnContainer>
        <SectionHeader>Preferences</SectionHeader>
        <Form>
          <Label>Gamer Tag:</Label>
          <Input
            type="text"
            name="gamerTag"
            value={preferences.gamerTag || ''}
            onChange={handleGamerTagChange}
          />

          <Label>
            <Checkbox
              type="checkbox"
              checked={checkAll}
              onChange={handleCheckAll}
            />
            Select/Unselect All
          </Label>

          <SectionHeader>Difficulty</SectionHeader>
          {versionConfig.difficulty && versionConfig.difficulty.map((item, index) => (
            <Label key={index}>
              <Checkbox
                type="checkbox"
                checked={preferences.difficulty[index] || false}
                onChange={() => handleChange('difficulty', index)}
              />
              {/* No icon for difficulty */}
              {item}
            </Label>
          ))}

          <SectionHeader>Primary Weapons</SectionHeader>
          {versionConfig.primary && versionConfig.primary.map((item, index) => (
            <Label key={index}>
              <Checkbox
                type="checkbox"
                checked={preferences.primary[index] || false}
                onChange={() => handleChange('primary', index)}
              />
              {iconPaths.primary[item] && (
                <img src={iconPaths.primary[item]} alt={item} style={{ width: 32, height: 32, marginRight: 8 }} />
              )}
              {item}
            </Label>
          ))}

          <SectionHeader>Secondary Weapons</SectionHeader>
          {versionConfig.secondary && versionConfig.secondary.map((item, index) => (
            <Label key={index}>
              <Checkbox
                type="checkbox"
                checked={preferences.secondary[index] || false}
                onChange={() => handleChange('secondary', index)}
              />
              {iconPaths.secondary[item] && (
                <img src={iconPaths.secondary[item]} alt={item} style={{ width: 32, height: 32, marginRight: 8 }} />
              )}
              {item}
            </Label>
          ))}

          <SectionHeader>Throwable</SectionHeader>
          {versionConfig.grenade && versionConfig.grenade.map((item, index) => (
            <Label key={index}>
              <Checkbox
                type="checkbox"
                checked={preferences.grenade[index] || false}
                onChange={() => handleChange('grenade', index)}
              />
              {iconPaths.grenade[item] && (
                <img src={iconPaths.grenade[item]} alt={item} style={{ width: 32, height: 32, marginRight: 8 }} />
              )}
              {item}
            </Label>
          ))}

          <SectionHeader>Stratagems</SectionHeader>
          {versionConfig.stratagem && versionConfig.stratagem.map((item, index) => (
            <Label key={index}>
              <Checkbox
                type="checkbox"
                checked={preferences.stratagem[index] || false}
                onChange={() => handleChange('stratagem', index)}
              />
              {iconPaths.stratagem[item] && (
                <img src={iconPaths.stratagem[item]} alt={item} style={{ width: 32, height: 32, marginRight: 8 }} />
              )}
              {item}
            </Label>
          ))}
        </Form>
      </ColumnContainer>
    </PageContainer>
  );
};

export default PreferencesView;
